import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  UserInfo,
  LoginResponse,
  saveAuthData,
  getStoredUser,
  getStoredToken,
  clearAuthData,
  isAdmin as checkIsAdmin,
  isStaff as checkIsStaff,
  isTokenExpired,
} from '../services/auth';
import { AvailableToolsResponse, getAvailableTools } from '../services/tools';

// ==================== INTERFACES ====================

interface AuthContextType {
  user: UserInfo | null;
  token: string | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isStaff: boolean;
  isLoading: boolean;
  tools: AvailableToolsResponse | null;
  login: (response: LoginResponse) => void;
  logout: () => void;
  refreshTools: () => Promise<void>;
  checkSession: () => boolean;
}

// ==================== CONTEXT ====================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ==================== PROVIDER ====================

// Intervalo de verificação de sessão (1 minuto)
const SESSION_CHECK_INTERVAL = 60 * 1000;

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tools, setTools] = useState<AvailableToolsResponse | null>(null);
  const sessionCheckRef = useRef<NodeJS.Timeout | null>(null);

  // Função para redirecionar para login
  const redirectToLogin = useCallback(() => {
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }, []);

  // Carregar dados do localStorage na inicialização
  useEffect(() => {
    const storedUser = getStoredUser();
    const storedToken = getStoredToken();
    
    if (storedToken && storedUser) {
      // Verificar se o token não está expirado
      if (!isTokenExpired()) {
        setUser(storedUser);
        setToken(storedToken);
      } else {
        // Token expirado, limpar dados e redirecionar
        clearAuthData();
        redirectToLogin();
      }
    }
    
    setIsLoading(false);
  }, [redirectToLogin]);

  // Carregar ferramentas quando o usuário estiver autenticado
  useEffect(() => {
    if (token && user) {
      refreshTools();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user]);

  const login = useCallback((response: LoginResponse) => {
    saveAuthData(response);
    if (response.user) {
      setUser(response.user);
    }
    if (response.token) {
      setToken(response.token);
    }
  }, []);

  const logout = useCallback(() => {
    clearAuthData();
    setUser(null);
    setToken(null);
    setTools(null);
    // Limpar o intervalo de verificação
    if (sessionCheckRef.current) {
      clearInterval(sessionCheckRef.current);
      sessionCheckRef.current = null;
    }
  }, []);

  // Verificar se a sessão ainda é válida
  const checkSession = useCallback((): boolean => {
    if (!token || isTokenExpired()) {
      logout();
      redirectToLogin();
      return false;
    }
    return true;
  }, [token, logout, redirectToLogin]);

  // Verificar sessão periodicamente
  useEffect(() => {
    if (token && user) {
      // Verificar imediatamente
      if (isTokenExpired()) {
        logout();
        redirectToLogin();
        return;
      }

      // Configurar verificação periódica
      sessionCheckRef.current = setInterval(() => {
        if (isTokenExpired()) {
          console.log('Sessão expirada. Redirecionando para login...');
          logout();
          redirectToLogin();
        }
      }, SESSION_CHECK_INTERVAL);

      // Também verificar quando a aba volta a ter foco
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible' && isTokenExpired()) {
          console.log('Sessão expirada ao retornar à aba. Redirecionando para login...');
          logout();
          redirectToLogin();
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        if (sessionCheckRef.current) {
          clearInterval(sessionCheckRef.current);
        }
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [token, user, logout, redirectToLogin]);

  const refreshTools = useCallback(async () => {
    try {
      // Verificar sessão antes de fazer a requisição
      if (!checkSession()) return;
      
      const toolsData = await getAvailableTools();
      setTools(toolsData);
    } catch (error) {
      console.error('Erro ao carregar ferramentas:', error);
      // Se der erro de autenticação, fazer logout
      if (error instanceof Error && error.message.includes('Sessão expirada')) {
        logout();
        redirectToLogin();
      }
    }
  }, [logout, redirectToLogin, checkSession]);

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!token && !!user,
    isAdmin: checkIsAdmin(user),
    isStaff: checkIsStaff(user),
    isLoading,
    tools,
    login,
    logout,
    refreshTools,
    checkSession,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// ==================== HOOK ====================

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

export default AuthContext;

